import { Add, Close, Delete, Edit } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addPaymentConfiguration,
  addPaymentConfigRules,
  addProduct,
  deactivateRule,
  editPaymentConfiguration,
  editPaymentConfigRule,
  editProduct,
  fetchAllPaymentConfigRules,
  fetchAllPaymentConfigurations,
  fetchAllProducts,
  fetchMappingsByProduct,
  removeAllMappings,
  removeSingleMapping,
  removePaymentConfigRule,
  removePaymentConfiguration,
  removeProduct,
  saveMappings,
  selectMappings,
  selectPaymentConfigRules,
  selectPaymentConfigurations,
  selectPlatformLoading,
  selectProducts,
  selectWizard,
  setWizardConfigurationId,
  setWizardProductId,
} from "../../../../Redux/Slices/platformSlice";
import {
  ErrorMessage,
  SuccessMessage,
} from "../../../../commons/Toast/ErrorMessage";
import {
  getConsumersByCompId,
  getGeneratorsByCompId,
} from "../../../../Services/Admin/Platform";
import {
  getAllConsumerDetailsSuperAdmin,
  getAllGeneratorDetailsSuperAdmin,
} from "../../../../Services/GNA/AlterApi";
import Styles from "./ConfigurePlatform.module.css";
import { fetchCompany } from "../../../../Redux/Slices/gnaAdminSlice";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";

// ─── Constants ────────────────────────────────────────────────────────────────
const PRODUCT_TYPES = ["RA", "RFP_RFQ", "BILATERAL", "TRADE_CONNECT"];

const PRODUCT_FORM_INIT = { productType: "RA", comment: "" };
const CONFIG_FORM_INIT = { configurationName: "", comment: "" };
const RULE_FORM_INIT = {
  listingType: "",
  paymentConfigurationId: "",
  minScheduleDays: 0,
  maxScheduleDays: 0,
  scheduleDayFrom: 0,
  scheduleDayTo: 0,
  relativeDayFrom: "",
  relativeDayTo: "",
  ruleType: "",
  triggerOffsetDays: 0,
  triggerReferenceType: "",
  percentage: 0,
  percentageSource: "",
  paymentType: "",
  transactionType: "",
  slidingStartDay: 0,
  version: 0,
  scopeType: "",
  scopeId: 0,
  entityType: "",
};

// ─── Reusable Field Components ────────────────────────────────────────────────
const FormField = ({ label, name, value, onChange, type = "text", placeholder = "" }) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <input
      className={Styles.fieldInput}
      name={name}
      value={value}
      onChange={onChange}
      type={type}
      placeholder={placeholder}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options }) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <select className={Styles.fieldSelect} name={name} value={value} onChange={onChange}>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

const DataSelect = ({ label, name, value, onChange, items, getValue, getLabel, onClick }) => (
  <div className={Styles.field}>
    <label className={Styles.fieldLabel}>{label}</label>
    <select
      className={Styles.fieldSelect}
      name={name}
      value={value}
      onChange={onChange}
      onClick={onClick}
    >
      <option value="">Select…</option>
      {items.map((item) => (
        <option key={getValue(item)} value={getValue(item)}>{getLabel(item)}</option>
      ))}
    </select>
  </div>
);

// ─── Chip ─────────────────────────────────────────────────────────────────────
const Chip = ({ label, variant = "teal" }) => (
  <span className={`${Styles.chip} ${Styles[`chip_${variant}`]}`}>{label}</span>
);

// ─── Form Dialog (drawer style) ───────────────────────────────────────────────
const FormDialog = ({ open, onClose, title, subtitle, onSubmit, submitLabel, children }) => {
  if (!open) return null;
  return (
    <div className={Styles.dialogOverlay} onClick={onClose}>
      <div className={Styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={Styles.dialogHeader}>
          <div>
            <div className={Styles.dialogTitle}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: "#8c8a96", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className={Styles.dialogClose} onClick={onClose}>
            <Close fontSize="small" />
          </button>
        </div>
        <div className={Styles.dialogBody}>{children}</div>
        <div className={Styles.dialogFooter}>
          <button className={Styles.btnGhost} onClick={onClose}>Cancel</button>
          <button className={Styles.btnPrimary} onClick={onSubmit}>{submitLabel}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Data Table ───────────────────────────────────────────────────────────────
const DataTable = ({ columns, rows, renderRow, stickyHeader = true }) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <table className={Styles.table}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col} className={Styles.th}>{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {safeRows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className={Styles.emptyCell}>No records found</td>
          </tr>
        ) : (
          safeRows.map((row, i) => (
            <tr key={row.id ?? i} className={Styles.tr}>{renderRow(row)}</tr>
          ))
        )}
      </tbody>
    </table>
  );
};

// ─── Products Tab ─────────────────────────────────────────────────────────────
const ProductsTab = ({ products, dispatch, wizard }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(PRODUCT_FORM_INIT);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const openAdd = () => { setEditId(null); setForm(PRODUCT_FORM_INIT); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({ productType: row.productType, comment: row.comment || "" });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    const action = editId
      ? dispatch(editProduct({ ...form, id: editId }))
      : dispatch(addProduct(form));
    action
      .unwrap()
      .then(() => SuccessMessage(editId ? "Product updated" : "Product created"))
      .catch((err) => ErrorMessage(err || "Failed to save product"));
    setDialogOpen(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div className={Styles.paneHd}>
        <div>
          <div className={Styles.paneTitle}>Products</div>
          <div className={Styles.paneDesc}>Product types available on the platform. Set one active to scope mappings and rules to it.</div>
        </div>
        <div className={Styles.paneActs}>
          <button className={Styles.btnPrimary} onClick={openAdd}>
            <Add style={{ fontSize: 16 }} /> Add product
          </button>
        </div>
      </div>

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? "Edit Product" : "Add product"}
        subtitle="A product type entities can be mapped to."
        onSubmit={handleSubmit}
        submitLabel={editId ? "Update" : "Add product"}
      >
        <div className={Styles.fgroup}>
          <div className={Styles.formGrid}>
            <SelectField label="Product Type" name="productType" value={form.productType} onChange={handleChange} options={PRODUCT_TYPES} />
            <FormField label="Comment" name="comment" value={form.comment} onChange={handleChange} placeholder="e.g. demo" />
          </div>
        </div>
      </FormDialog>

      <div className={`${Styles.card} ${Styles.cardFill}`}>
        <div className={Styles.tableScroll}>
          <DataTable
            columns={["ID", "Product type", "Comment", "Actions"]}
            rows={products}
            renderRow={(row) => (
              <>
                <td className={`${Styles.td} ${Styles.tdNum}`}>{row.id}</td>
                <td className={Styles.td}><span className={Styles.codeChip}>{row.productType}</span></td>
                <td className={Styles.td} style={{ color: row.comment ? "#525062" : "#a9a8b1" }}>{row.comment || "—"}</td>
                <td className={Styles.td}>
                  <div className={Styles.rowActs} style={{ justifyContent: "flex-end", alignItems: "center", gap: 6 }}>
                    <button
                      className={`${Styles.useBtn} ${wizard.productId === row.id ? Styles.useBtnActive : ""}`}
                      onClick={() => dispatch(setWizardProductId(wizard.productId === row.id ? null : row.id))}
                    >
                      {wizard.productId === row.id ? "✓ Active" : "Set active"}
                    </button>
                    <button className={Styles.iconBtn} onClick={() => openEdit(row)} title="Edit">
                      <Edit style={{ fontSize: 15 }} />
                    </button>
                    <button
                      className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`}
                      onClick={() => dispatch(removeProduct(row.id)).unwrap().then(() => SuccessMessage("Product deleted")).catch((err) => ErrorMessage(err || "Failed to delete"))}
                      title="Delete"
                    >
                      <Delete style={{ fontSize: 15 }} />
                    </button>
                  </div>
                </td>
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Payment Split Tab ─────────────────────────────────────────────────────────
const PaymentSplitTab = ({ configurations, rules, dispatch, wizard }) => {
  const [cfgSearch, setCfgSearch] = useState("");
  const [cfgDialogOpen, setCfgDialogOpen] = useState(false);
  const [cfgEditId, setCfgEditId] = useState(null);
  const [cfgForm, setCfgForm] = useState(CONFIG_FORM_INIT);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleEditId, setRuleEditId] = useState(null);
  const [ruleForm, setRuleForm] = useState({ ...RULE_FORM_INIT, paymentConfigurationId: wizard.configurationId || "" });

  useEffect(() => {
    if (wizard.configurationId) setRuleForm((f) => ({ ...f, paymentConfigurationId: wizard.configurationId }));
  }, [wizard.configurationId]);

  const handleCfgChange = (e) => {
    const { name, value } = e.target;
    const sanitized = name === "configurationName" ? value.replace(/[^A-Z0-9_]/g, "") : value;
    setCfgForm((f) => ({ ...f, [name]: sanitized }));
  };

  const openCfgAdd = () => { setCfgEditId(null); setCfgForm(CONFIG_FORM_INIT); setCfgDialogOpen(true); };
  const openCfgEdit = (cfg) => {
    setCfgEditId(cfg.id);
    setCfgForm({ configurationName: cfg.configurationName, comment: cfg.comment || "" });
    setCfgDialogOpen(true);
  };

  const handleCfgSubmit = () => {
    const action = cfgEditId
      ? dispatch(editPaymentConfiguration({ ...cfgForm, id: cfgEditId }))
      : dispatch(addPaymentConfiguration(cfgForm));
    action.unwrap().then(() => SuccessMessage(cfgEditId ? "Configuration updated" : "Configuration created")).catch((err) => ErrorMessage(err || "Failed"));
    setCfgDialogOpen(false);
  };

  const handleRuleChange = (e) => setRuleForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const openRuleAdd = () => {
    setRuleEditId(null);
    setRuleForm({ ...RULE_FORM_INIT, paymentConfigurationId: wizard.configurationId || "" });
    setRuleDialogOpen(true);
  };
  const openRuleEdit = (row) => { setRuleEditId(row.id); setRuleForm({ ...row }); setRuleDialogOpen(true); };

  const handleRuleSubmit = () => {
    const action = ruleEditId
      ? dispatch(editPaymentConfigRule({ ...ruleForm, id: ruleEditId }))
      : dispatch(addPaymentConfigRules([{ ...ruleForm }]));
    action.unwrap().then(() => SuccessMessage(ruleEditId ? "Rule updated" : "Rule created")).catch((err) => ErrorMessage(err || "Failed"));
    setRuleDialogOpen(false);
    setRuleEditId(null);
  };

  const filteredConfigs = (configurations || []).filter((c) =>
    (c.configurationName || "").toLowerCase().includes(cfgSearch.toLowerCase())
  );

  const activeRules = (rules || []).filter((r) => r.paymentConfigurationId === wizard.configurationId);
  const selectedCfg = (configurations || []).find((c) => c.id === wizard.configurationId);
  const ruleCountFor = (cfgId) => (rules || []).filter((r) => r.paymentConfigurationId === cfgId).length;

  return (
    <>
      {/* Config dialog */}
      <FormDialog
        open={cfgDialogOpen}
        onClose={() => setCfgDialogOpen(false)}
        title={cfgEditId ? "Edit Configuration" : "New payment configuration"}
        subtitle="A named container for a set of payment rules."
        onSubmit={handleCfgSubmit}
        submitLabel={cfgEditId ? "Update" : "Create configuration"}
      >
        <div className={Styles.fgroup}>
          <div className={Styles.formGrid}>
            <FormField label="Configuration Name" name="configurationName" value={cfgForm.configurationName} onChange={handleCfgChange} placeholder="e.g. ODISHA_700" />
            <FormField label="Comment" name="comment" value={cfgForm.comment} onChange={handleCfgChange} placeholder="optional note" />
          </div>
        </div>
      </FormDialog>

      {/* Rule dialog */}
      <FormDialog
        open={ruleDialogOpen}
        onClose={() => { setRuleDialogOpen(false); setRuleEditId(null); }}
        title={ruleEditId ? "Edit config rule" : "Add config rule"}
        subtitle={selectedCfg ? `To configuration: ${selectedCfg.configurationName}` : undefined}
        onSubmit={handleRuleSubmit}
        submitLabel={ruleEditId ? "Update rule" : "Add rule"}
      >
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>Definition</div>
          <div className={Styles.fgroupDesc}>What kind of rule this is and which transactions it applies to.</div>
          <div className={Styles.grid2}>
            <FormField label="Listing Type" name="listingType" value={ruleForm.listingType} onChange={handleRuleChange} placeholder="e.g. DAM" />
            <FormField label="Rule Type" name="ruleType" value={ruleForm.ruleType} onChange={handleRuleChange} placeholder="e.g. FIXED" />
            <FormField label="Payment Type" name="paymentType" value={ruleForm.paymentType} onChange={handleRuleChange} placeholder="e.g. FUNDS_PAYIN" />
            <FormField label="Transaction Type" name="transactionType" value={ruleForm.transactionType} onChange={handleRuleChange} placeholder="e.g. BUY" />
          </div>
        </div>
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>Schedule</div>
          <div className={Styles.fgroupDesc}>When the payment falls due, in days relative to the trade or delivery date.</div>
          <div className={Styles.grid2}>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Relative Day Window</label>
              <div className={Styles.rangeRow}>
                <input className={Styles.fieldInput} name="relativeDayFrom" value={ruleForm.relativeDayFrom} onChange={handleRuleChange} placeholder="From" style={{ textAlign: "center" }} />
                <span className={Styles.rangeSep}>→</span>
                <input className={Styles.fieldInput} name="relativeDayTo" value={ruleForm.relativeDayTo} onChange={handleRuleChange} placeholder="To" style={{ textAlign: "center" }} />
              </div>
            </div>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Schedule Day Window</label>
              <div className={Styles.rangeRow}>
                <input className={Styles.fieldInput} name="scheduleDayFrom" type="number" value={ruleForm.scheduleDayFrom} onChange={handleRuleChange} style={{ textAlign: "center" }} />
                <span className={Styles.rangeSep}>→</span>
                <input className={Styles.fieldInput} name="scheduleDayTo" type="number" value={ruleForm.scheduleDayTo} onChange={handleRuleChange} style={{ textAlign: "center" }} />
              </div>
            </div>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Schedule Length (min → max days)</label>
              <div className={Styles.rangeRow}>
                <input className={Styles.fieldInput} name="minScheduleDays" type="number" value={ruleForm.minScheduleDays} onChange={handleRuleChange} style={{ textAlign: "center" }} />
                <span className={Styles.rangeSep}>→</span>
                <input className={Styles.fieldInput} name="maxScheduleDays" type="number" value={ruleForm.maxScheduleDays} onChange={handleRuleChange} style={{ textAlign: "center" }} />
              </div>
            </div>
            <FormField label="Sliding Start Day" name="slidingStartDay" type="number" value={ruleForm.slidingStartDay} onChange={handleRuleChange} />
          </div>
        </div>
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>Trigger &amp; Amount</div>
          <div className={Styles.fgroupDesc}>What starts the collection and how much is collected.</div>
          <div className={Styles.grid3}>
            <FormField label="Percentage" name="percentage" type="number" value={ruleForm.percentage} onChange={handleRuleChange} placeholder="0–100" />
            <FormField label="Percentage Source" name="percentageSource" value={ruleForm.percentageSource} onChange={handleRuleChange} />
            <FormField label="Trigger Offset Days" name="triggerOffsetDays" type="number" value={ruleForm.triggerOffsetDays} onChange={handleRuleChange} />
          </div>
          <div style={{ marginTop: 14, maxWidth: 280 }}>
            <FormField label="Trigger Reference" name="triggerReferenceType" value={ruleForm.triggerReferenceType} onChange={handleRuleChange} placeholder="e.g. Trade date" />
          </div>
        </div>
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>Scope</div>
          <div className={Styles.fgroupDesc}>Optional — narrow the rule to an entity type or specific scope.</div>
          <div className={Styles.grid3}>
            <FormField label="Entity Type" name="entityType" value={ruleForm.entityType} onChange={handleRuleChange} placeholder="e.g. CONSUMER" />
            <FormField label="Scope Type" name="scopeType" value={ruleForm.scopeType} onChange={handleRuleChange} />
            <FormField label="Scope ID" name="scopeId" type="number" value={ruleForm.scopeId} onChange={handleRuleChange} />
          </div>
          <div style={{ marginTop: 14, maxWidth: 160 }}>
            <FormField label="Version" name="version" type="number" value={ruleForm.version} onChange={handleRuleChange} />
          </div>
        </div>
      </FormDialog>

      <div className={Styles.paneHd}>
        <div>
          <div className={Styles.paneTitle}>Payment configurations &amp; rules</div>
          <div className={Styles.paneDesc}>Pick a configuration on the left to view and edit the rules that belong to it.</div>
        </div>
        <div className={Styles.paneActs}>
          <button className={Styles.btnGhost} onClick={openCfgAdd}>
            <Add style={{ fontSize: 16 }} /> New configuration
          </button>
        </div>
      </div>

      <div className={Styles.split}>
        {/* Left: config list */}
        <div className={Styles.cfgListCard}>
          <div className={Styles.cfgListSearch}>
            <div style={{ position: "relative" }}>
              <span className={Styles.searchIcon} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#8c8a96" }}>
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.5" /><path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              </span>
              <input
                className={Styles.searchInput}
                style={{ paddingLeft: 30 }}
                placeholder="Filter configurations…"
                value={cfgSearch}
                onChange={(e) => setCfgSearch(e.target.value)}
              />
            </div>
          </div>
          <div className={Styles.cfgScroll}>
            {filteredConfigs.map((cfg) => (
              <button
                key={cfg.id}
                className={`${Styles.cfgItem} ${wizard.configurationId === cfg.id ? Styles.cfgItemActive : ""}`}
                onClick={() => dispatch(wizard.configurationId === cfg.id ? setWizardConfigurationId(null) : setWizardConfigurationId(cfg.id))}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className={Styles.cfgName}>{cfg.configurationName}</span>
                  <span className={Styles.cfgSubId}>#{cfg.id}</span>
                  <div className={Styles.cfgComment}>{cfg.comment && cfg.comment !== "NA" ? cfg.comment : "No comment"}</div>
                </span>
                <span className={Styles.cfgRuleCount}>{ruleCountFor(cfg.id)} rule{ruleCountFor(cfg.id) !== 1 ? "s" : ""}</span>
                <div style={{ display: "flex", gap: 2, marginLeft: 4 }}>
                  <button
                    className={Styles.iconBtn}
                    onClick={(e) => { e.stopPropagation(); openCfgEdit(cfg); }}
                    title="Edit"
                    style={{ width: 26, height: 26 }}
                  >
                    <Edit style={{ fontSize: 13 }} />
                  </button>
                  <button
                    className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(removePaymentConfiguration(cfg.id)).unwrap().then(() => SuccessMessage("Configuration deleted")).catch((err) => ErrorMessage(err || "Failed"));
                    }}
                    title="Delete"
                    style={{ width: 26, height: 26 }}
                  >
                    <Delete style={{ fontSize: 13 }} />
                  </button>
                </div>
              </button>
            ))}
            {filteredConfigs.length === 0 && (
              <div className={Styles.emptyState}>
                <div className={Styles.emptyDesc}>No configurations found</div>
              </div>
            )}
          </div>
        </div>

        {/* Right: rules detail */}
        <div className={Styles.rulesPanel}>
          <div className={Styles.rulesPanelHd}>
            <div className={Styles.rulesPanelTitle}>
              {selectedCfg
                ? <><span className={Styles.codeChip}>{selectedCfg.configurationName}</span> rules</>
                : <span style={{ color: "#8c8a96", fontWeight: 400, fontSize: 14 }}>Select a configuration</span>
              }
            </div>
            {wizard.configurationId && (
              <button className={`${Styles.btnPrimary} ${Styles.btnSm}`} onClick={openRuleAdd}>
                <Add style={{ fontSize: 14 }} /> Add rule
              </button>
            )}
          </div>
          <div className={Styles.tableScroll}>
            {!wizard.configurationId ? (
              <div className={Styles.emptyState}>
                <div className={Styles.emptyGlyph}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 11h14M11 4v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                </div>
                <div className={Styles.emptyTitle}>No configuration selected</div>
                <div className={Styles.emptyDesc}>Click a configuration on the left to view its rules.</div>
              </div>
            ) : activeRules.length === 0 ? (
              <div className={Styles.emptyState}>
                <div className={Styles.emptyGlyph}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4 11h14M11 4v14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                </div>
                <div className={Styles.emptyTitle}>No rules yet</div>
                <div className={Styles.emptyDesc}>Rules define when and how much is collected. Add one to make this configuration usable.</div>
                <button className={Styles.btnPrimary} onClick={openRuleAdd}><Add style={{ fontSize: 15 }} /> Add the first rule</button>
              </div>
            ) : (
              <DataTable
                columns={["ID", "Rule", "Payment", "Trigger", "%", "Actions"]}
                rows={activeRules}
                renderRow={(row) => (
                  <>
                    <td className={`${Styles.td} ${Styles.tdNum}`}>{row.id}</td>
                    <td className={Styles.td}><Chip label={row.ruleType || "—"} variant="teal" /></td>
                    <td className={Styles.td}><Chip label={row.paymentType || "—"} variant="orange" /></td>
                    <td className={Styles.td} style={{ fontSize: 12, fontFamily: "monospace", color: "#6f6d7c" }}>{row.triggerReferenceType || "—"}</td>
                    <td className={Styles.td} style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.percentage != null ? `${row.percentage}%` : "—"}</td>
                    <td className={Styles.td}>
                      <div className={Styles.rowActs} style={{ alignItems: "center", gap: 8 }}>
                        <button
                          className={`${Styles.ruleToggle} ${Styles.ruleToggleOn}`}
                          onClick={() =>
                            dispatch(deactivateRule(row.id)).unwrap().then(() => SuccessMessage("Rule deactivated")).catch((err) => ErrorMessage(err || "Failed"))
                          }
                          title="Deactivate rule"
                        />
                        <button className={Styles.iconBtn} onClick={() => openRuleEdit(row)} title="Edit"><Edit style={{ fontSize: 14 }} /></button>
                        <button
                          className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`}
                          onClick={() => dispatch(removePaymentConfigRule(row.id)).unwrap().then(() => SuccessMessage("Rule deleted")).catch((err) => ErrorMessage(err || "Failed"))}
                          title="Delete"
                        ><Delete style={{ fontSize: 14 }} /></button>
                      </div>
                    </td>
                  </>
                )}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Mappings Tab ─────────────────────────────────────────────────────────────
const MappingsTab = ({ allMappings, products, configurations, companies, rules, dispatch }) => {
  const wizard = useSelector(selectWizard);

  const [viewProductId, setViewProductId] = useState(wizard.productId || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editConfigId, setEditConfigId] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companySelectError, setCompanySelectError] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const companyDropdownRef = useRef(null);
  const [consumers, setConsumers] = useState([]);
  const [generators, setGenerators] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entityTypeFilter, setEntityTypeFilter] = useState(null); // "CONSUMER" | "GENERATOR" | null
  const [entityKeys, setEntityKeys] = useState([]);
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");
  const entityDropdownRef = useRef(null);
  const [selProductId, setSelProductId] = useState(wizard.productId || "");
  const [selConfigId, setSelConfigId] = useState(wizard.configurationId || "");
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [entries, setEntries] = useState([]);
  const fetchedCompaniesRef = useRef(new Set());
  const abortControllerRef = useRef(null);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [entityNameMap, setEntityNameMap] = useState({});
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const productDropdownRef = useRef(null);

  useEffect(() => {
    if (wizard.productId) { setViewProductId(wizard.productId); setSelProductId(wizard.productId); }
  }, [wizard.productId]);

  useEffect(() => {
    if (viewProductId) dispatch(fetchMappingsByProduct(viewProductId));
  }, [viewProductId, dispatch]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target)) setCompanyDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (entityDropdownRef.current && !entityDropdownRef.current.contains(e.target)) setEntityDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target)) setProductDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!viewProductId && products?.length) {
      const bilateral = products.find((p) => p.productType === "BILATERAL");
      if (bilateral) setViewProductId(bilateral.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    if (companyIds.length === 0) {
      setConsumers([]); setGenerators([]); fetchedCompaniesRef.current.clear(); return;
    }
    const effectiveIds = companyIds.includes("ALL") ? (companies || []).map((c) => c.companyId) : companyIds;
    if (effectiveIds.length === 0) return;
    const newIds = effectiveIds.filter((id) => !fetchedCompaniesRef.current.has(id));
    if (newIds.length === 0) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    setLoadingEntities(true);
    Promise.all(
      newIds.map((id) =>
        Promise.all([
          getConsumersByCompId(id).catch(() => ({ data: { data: [] } })),
          getGeneratorsByCompId(id).catch(() => ({ data: { data: [] } })),
        ]).then(([cRes, gRes]) => ({
          companyId: id,
          consumers: cRes?.data?.data || cRes?.data || [],
          generators: gRes?.data?.data || gRes?.data || [],
        }))
      )
    ).then((results) => {
      newIds.forEach((id) => fetchedCompaniesRef.current.add(id));
      const allC = results.flatMap((r) => r.consumers.map((c) => ({ ...c, _companyId: r.companyId })));
      const allG = results.flatMap((r) => r.generators.map((g) => ({ ...g, _companyId: r.companyId })));
      setConsumers((prev) => [...prev, ...allC]);
      setGenerators((prev) => [...prev, ...allG]);
    }).catch((err) => { if (err.name !== "AbortError") console.error(err); }).finally(() => setLoadingEntities(false));
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, [companyIds]);

  const currentMappings = allMappings[viewProductId] || [];

  useEffect(() => {
    if (currentMappings.length === 0) return;
    const missing = currentMappings.filter((m) => !entityNameMap[m.entityId]);
    if (missing.length === 0) return;
    const missingConsumerIds = missing.filter((m) => m.entityType === "CONSUMER").map((m) => m.entityId);
    const missingGeneratorIds = missing.filter((m) => m.entityType === "GENERATOR").map((m) => m.entityId);
    if (missingConsumerIds.length > 0) {
      getAllConsumerDetailsSuperAdmin(missingConsumerIds).then((res) => {
        const list = res?.data?.data || [];
        const updates = {};
        (Array.isArray(list) ? list : [list]).forEach((c) => { if (c.consumerId && c.consumerName) updates[c.consumerId] = c.consumerName; });
        if (Object.keys(updates).length) setEntityNameMap((prev) => ({ ...prev, ...updates }));
      }).catch(() => {});
    }
    if (missingGeneratorIds.length > 0) {
      getAllGeneratorDetailsSuperAdmin(missingGeneratorIds).then((res) => {
        const list = res?.data?.data || [];
        const updates = {};
        (Array.isArray(list) ? list : [list]).forEach((g) => { if (g.generatorId && g.generatorName) updates[g.generatorId] = g.generatorName; });
        if (Object.keys(updates).length) setEntityNameMap((prev) => ({ ...prev, ...updates }));
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMappings]);

  const allEntities = [
    ...consumers.map((c) => ({ key: `C:${c.consumerId}`, id: c.consumerId, name: c.consumerName || String(c.consumerId), type: "CONSUMER", companyId: c._companyId })),
    ...generators.map((g) => ({ key: `G:${g.generatorId}`, id: g.generatorId, name: g.generatorName || String(g.generatorId), type: "GENERATOR", companyId: g._companyId })),
  ];
  const selectedEntities = allEntities.filter((e) => entityKeys.includes(e.key));
  const canAdd = companyIds.length > 0 && entityKeys.length > 0 && selProductId && selConfigId;

  const handleAddEntry = () => {
    if (!canAdd || selectedEntities.length === 0) return;
    setEntries((prev) => {
      const existingKeys = new Set(prev.map((e) => `${e.entityId}|${e.entityType}|${e.configurationId}`));
      const newEntries = selectedEntities
        .filter((entity) => !existingKeys.has(`${entity.id}|${entity.type}|${Number(selConfigId)}`))
        .map((entity) => ({ configurationId: Number(selConfigId), entityId: entity.id, entityType: entity.type, companyId: entity.companyId }));
      return [...prev, ...newEntries];
    });
    setEntityKeys([]);
  };

  const removeEntry = (i) => setEntries((prev) => prev.filter((_, idx) => idx !== i));

  const openDialog = () => {
    dispatch(fetchAllProducts()); dispatch(fetchAllPaymentConfigurations()); dispatch(fetchAllPaymentConfigRules());
    setCompanyIds([]); setCompanyDropdownOpen(false); setCompanySelectError(""); setCompanySearch("");
    setConsumers([]); setGenerators([]); fetchedCompaniesRef.current.clear();
    setEntityTypeFilter(null); setEntityKeys([]); setEntityDropdownOpen(false); setEntitySearch("");
    setSelProductId(wizard.productId || viewProductId || ""); setSelConfigId(wizard.configurationId || "");
    setReplaceExisting(false); setEntries([]); setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!selProductId || entries.length === 0) return;
    const payload = { productId: Number(selProductId), replaceExisting, mappings: entries.map((e) => ({ configurationId: e.configurationId, entityId: e.entityId, entityType: e.entityType, companyId: e.companyId })) };
    dispatch(saveMappings({ productId: selProductId, payload }))
      .unwrap().then(() => SuccessMessage("Mappings saved")).catch((err) => ErrorMessage(err || "Failed"));
    setViewProductId(selProductId);
    setDialogOpen(false);
  };

  const handleDeleteSingle = (row) => {
    dispatch(removeSingleMapping({ productId: viewProductId, payload: { configurationId: row.configurationId, entityId: row.entityId, entityType: row.entityType } }))
      .unwrap().then(() => SuccessMessage("Mapping removed")).catch((err) => ErrorMessage(err || "Failed"));
  };

  const handleDeleteAllConfirm = () => {
    if (!viewProductId) return;
    dispatch(removeAllMappings(viewProductId)).unwrap()
      .then(() => { SuccessMessage("All mappings cleared"); setDeleteAllConfirmOpen(false); })
      .catch((err) => ErrorMessage(err || "Failed"));
  };

  const filteredMappings = currentMappings.filter((m) => {
    const q = searchQuery.toLowerCase();
    return !q || (m.entityName || "").toLowerCase().includes(q) || (m.companyName || "").toLowerCase().includes(q) || (m.configurationName || "").toLowerCase().includes(q);
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <ConfirmationModal
        open={deleteAllConfirmOpen}
        handleClose={() => setDeleteAllConfirmOpen(false)}
        header="Confirm Delete All"
        data="Are you sure you want to delete all mappings for this product?"
        note="This action cannot be undone."
        handleSubmit={handleDeleteAllConfirm}
        loading={false}
      />

      <div className={Styles.paneHd}>
        <div>
          <div className={Styles.paneTitle}>Product configuration mappings</div>
          <div className={Styles.paneDesc}>Each mapping connects a company entity to a product and its payment configuration.</div>
        </div>
        <div className={Styles.paneActs}>
          {currentMappings.length > 0 && (
            <button className={Styles.btnDangerGhost} onClick={() => setDeleteAllConfirmOpen(true)}>
              <Delete style={{ fontSize: 15 }} /> Delete all
            </button>
          )}
          <button className={Styles.btnPrimary} onClick={openDialog}>
            <Add style={{ fontSize: 16 }} /> Add mappings
          </button>
        </div>
      </div>

      <div className={Styles.toolbar}>
        <div className={Styles.field} style={{ minWidth: 230, position: "relative" }} ref={productDropdownRef}>
          <div
            className={Styles.fieldSelect}
            onClick={() => setProductDropdownOpen((o) => !o)}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", minHeight: 38, userSelect: "none" }}
          >
            <span style={{ fontSize: 13, color: viewProductId ? "#333" : "#a9a8b1" }}>
              {viewProductId
                ? (() => { const p = (products || []).find((prod) => prod.id === viewProductId || prod.id === Number(viewProductId)); return p ? `${p.productType} (#${p.id})` : "All products"; })()
                : "All products"}
            </span>
          </div>
          {productDropdownOpen && (
            <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, minWidth: "100%", background: "#fff", border: "1px solid #ddd", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 200 }}>
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                <div
                  style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: !viewProductId ? "#effcff" : "#fff", fontSize: 13, color: !viewProductId ? "#34656d" : "#525062", fontWeight: !viewProductId ? 600 : 400, borderBottom: "1px solid #f5f5f5" }}
                  onClick={() => { setViewProductId(""); setProductDropdownOpen(false); }}
                >
                  All products
                </div>
                {(products || []).map((p) => {
                  const isSelected = viewProductId === p.id || viewProductId === String(p.id);
                  return (
                    <div
                      key={p.id}
                      style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: isSelected ? "#effcff" : "#fff", fontSize: 13, color: isSelected ? "#34656d" : "#525062", fontWeight: isSelected ? 600 : 400 }}
                      onClick={() => { setViewProductId(p.id); setProductDropdownOpen(false); }}
                    >
                      <span className={Styles.codeChip}>{p.productType}</span>
                      <span style={{ fontSize: 11, color: "#8c8a96", fontFamily: "monospace" }}>#{p.id}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className={Styles.searchBox}>
          <span className={Styles.searchIcon}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.5" /><path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </span>
          <input className={Styles.searchInput} placeholder="Search entity or company…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {/* Add mappings dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Add product configuration mappings"
        subtitle="Queue one or more mappings, then save them together."
        onSubmit={handleSubmit}
        submitLabel={`Save mappings${entries.length > 0 ? ` (${entries.length})` : ""}`}
      >
        {/* Step 1: Entity */}
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>
            <span style={{ background: "#34656d", color: "#fff", width: 20, height: 20, borderRadius: "50%", display: "inline-grid", placeItems: "center", fontSize: 10, fontWeight: 700 }}>1</span>
            &nbsp;Select Entity
          </div>
          {/* Row 1: Company + Entity Type toggle */}
          <div className={Styles.grid2}>
            {/* Company dropdown */}
            <div className={Styles.field} ref={companyDropdownRef} style={{ position: "relative" }}>
              <label className={Styles.fieldLabel}>Company</label>
              <div
                className={Styles.fieldSelect}
                onClick={() => setCompanyDropdownOpen((o) => !o)}
                style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 38, userSelect: "none" }}
              >
                <span style={{ fontSize: 13, color: companyIds.length === 0 ? "#a9a8b1" : "#333" }}>
                  {companyIds.length === 0 ? "Select company…" : companyIds.includes("ALL") ? "All Companies" : `${companyIds.length} selected`}
                </span>
                <span style={{ fontSize: 9 }}>▼</span>
              </div>
              {companyIds.length > 0 && !companyIds.includes("ALL") && companyIds.length <= 5 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                  {companyIds.map((cid) => {
                    const c = (companies || []).find((x) => x.companyId === cid);
                    return (
                      <span key={cid} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#effcff", color: "#34656d", border: "1px solid #91a8ac", borderRadius: 100, fontSize: 11, padding: "2px 8px" }}>
                        {c ? c.companyName || c.name || `#${cid}` : cid}
                        <Close style={{ fontSize: 12, cursor: "pointer" }} onClick={() => {
                          const keysToRemove = new Set(allEntities.filter((e) => e.companyId === cid).map((e) => e.key));
                          setCompanyIds((prev) => prev.filter((id) => id !== cid));
                          setEntityKeys((prev) => prev.filter((k) => !keysToRemove.has(k)));
                          setConsumers((prev) => prev.filter((c) => c._companyId !== cid));
                          setGenerators((prev) => prev.filter((g) => g._companyId !== cid));
                          fetchedCompaniesRef.current.delete(cid);
                          setCompanySelectError("");
                          setEntityTypeFilter(null);
                        }} />
                      </span>
                    );
                  })}
                </div>
              )}
              {companySelectError && <div style={{ color: "#d50d21", fontSize: 11, marginTop: 4 }}>{companySelectError}</div>}
              {companyDropdownOpen && (
                <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 200 }}>
                  <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "#fff" }}>
                    <input type="text" placeholder="Search company…" value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e2e2e5", borderRadius: 4, padding: "6px 10px", fontSize: 12, outline: "none", fontFamily: "inherit", color: "#333" }} />
                  </div>
                  <div style={{ maxHeight: 180, overflowY: "auto" }}>
                    {!companySearch && (
                      <div style={{ padding: "7px 10px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: companyIds.includes("ALL") ? "#fffaf6" : "#fff", borderBottom: "1px solid #f0f0f0", fontWeight: 600, fontSize: 12 }}
                        onClick={() => { setCompanyIds(companyIds.includes("ALL") ? [] : ["ALL"]); setEntityKeys([]); setEntityTypeFilter(null); setCompanySelectError(""); }}>
                        <input type="checkbox" checked={companyIds.includes("ALL")} readOnly style={{ accentColor: "#f1935c" }} />
                        All Companies
                      </div>
                    )}
                    {(companies || []).filter((c) => (c.companyName || c.name || "").toLowerCase().includes(companySearch.toLowerCase())).map((c) => {
                      const isSelected = companyIds.includes(c.companyId);
                      const allSelected = companyIds.includes("ALL");
                      return (
                        <div key={c.id} style={{ padding: "7px 10px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: isSelected || allSelected ? "#fffaf6" : "#fff", fontSize: 12 }}
                          onClick={() => {
                            if (allSelected) {
                              const expanded = (companies || []).map((x) => x.companyId).filter((id) => id !== c.companyId);
                              setCompanyIds(expanded);
                              const keys = new Set(allEntities.filter((e) => e.companyId === c.companyId).map((e) => e.key));
                              setEntityKeys((prev) => prev.filter((k) => !keys.has(k)));
                              setConsumers((prev) => prev.filter((con) => con._companyId !== c.companyId));
                              setGenerators((prev) => prev.filter((gen) => gen._companyId !== c.companyId));
                              fetchedCompaniesRef.current.delete(c.companyId);
                            } else if (isSelected) {
                              setCompanyIds((prev) => prev.filter((id) => id !== c.companyId));
                              const keys = new Set(allEntities.filter((e) => e.companyId === c.companyId).map((e) => e.key));
                              setEntityKeys((prev) => prev.filter((k) => !keys.has(k)));
                              setConsumers((prev) => prev.filter((con) => con._companyId !== c.companyId));
                              setGenerators((prev) => prev.filter((gen) => gen._companyId !== c.companyId));
                              fetchedCompaniesRef.current.delete(c.companyId);
                            } else {
                              setCompanyIds((prev) => [...prev, c.companyId]);
                              setEntityKeys([]);
                              setEntityTypeFilter(null);
                            }
                            setCompanySelectError("");
                          }}>
                          <input type="checkbox" checked={isSelected || allSelected} readOnly style={{ accentColor: "#f1935c" }} />
                          {c.companyName || c.name || `Company #${c.id}`}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Entity type toggle — visible after company selected */}
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Entity Type</label>
              {companyIds.length === 0 ? (
                <div style={{ fontSize: 12, color: "#a9a8b1", paddingTop: 10 }}>Select a company first</div>
              ) : loadingEntities ? (
                <div style={{ fontSize: 12, color: "#8c8a96", paddingTop: 10 }}>Loading…</div>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={() => { setEntityTypeFilter("CONSUMER"); setEntityKeys([]); setEntityDropdownOpen(false); setEntitySearch(""); }}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                      padding: "9px 12px", borderRadius: 8, border: "1px solid",
                      borderColor: entityTypeFilter === "CONSUMER" ? "#34656d" : "#e2e2e5",
                      background: entityTypeFilter === "CONSUMER" ? "#effcff" : "#fff",
                      color: entityTypeFilter === "CONSUMER" ? "#34656d" : "#6f6d7c",
                      fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: entityTypeFilter === "CONSUMER" ? "#34656d" : "#e2e2e5", color: entityTypeFilter === "CONSUMER" ? "#fff" : "#6f6d7c", display: "inline-grid", placeItems: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>C</span>
                    Consumer
                    <span style={{ marginLeft: "auto", fontSize: 10, fontFamily: "monospace", fontWeight: 400, color: "#8c8a96" }}>{consumers.length}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEntityTypeFilter("GENERATOR"); setEntityKeys([]); setEntityDropdownOpen(false); setEntitySearch(""); }}
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                      padding: "9px 12px", borderRadius: 8, border: "1px solid",
                      borderColor: entityTypeFilter === "GENERATOR" ? "#f1935c" : "#e2e2e5",
                      background: entityTypeFilter === "GENERATOR" ? "#fff4ee" : "#fff",
                      color: entityTypeFilter === "GENERATOR" ? "#f1935c" : "#6f6d7c",
                      fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ width: 18, height: 18, borderRadius: "50%", background: entityTypeFilter === "GENERATOR" ? "#f1935c" : "#e2e2e5", color: entityTypeFilter === "GENERATOR" ? "#fff" : "#6f6d7c", display: "inline-grid", placeItems: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>G</span>
                    Generator
                    <span style={{ marginLeft: "auto", fontSize: 10, fontFamily: "monospace", fontWeight: 400, color: "#8c8a96" }}>{generators.length}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Entity dropdown — shown after type is selected */}
          {companyIds.length > 0 && entityTypeFilter && (
            <div className={Styles.field} ref={entityDropdownRef} style={{ position: "relative", marginTop: 12 }}>
              <label className={Styles.fieldLabel}>
                Select {entityTypeFilter === "CONSUMER" ? "Consumer" : "Generator"}
                {entityKeys.length > 0 && <span style={{ marginLeft: 6, fontWeight: 400, color: "#8c8a96" }}>{entityKeys.length} selected</span>}
              </label>
              {(() => {
                const q = entitySearch.toLowerCase();
                const list = entityTypeFilter === "CONSUMER"
                  ? consumers.filter((c) => (c.consumerName || String(c.consumerId)).toLowerCase().includes(q))
                  : generators.filter((g) => (g.generatorName || String(g.generatorId)).toLowerCase().includes(q));
                const allKeys = entityTypeFilter === "CONSUMER"
                  ? list.map((c) => `C:${c.consumerId}`)
                  : list.map((g) => `G:${g.generatorId}`);
                const allSelected = allKeys.length > 0 && allKeys.every((k) => entityKeys.includes(k));
                const accent = entityTypeFilter === "CONSUMER" ? "#34656d" : "#f1935c";
                const bgSel = entityTypeFilter === "CONSUMER" ? "#effcff" : "#fff4ee";
                const badge = entityTypeFilter === "CONSUMER" ? "C" : "G";
                return (
                  <>
                    {/* Trigger */}
                    <div
                      className={Styles.fieldSelect}
                      onClick={() => setEntityDropdownOpen((o) => !o)}
                      style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 38, userSelect: "none" }}
                    >
                      <span style={{ fontSize: 13, color: entityKeys.length === 0 ? "#a9a8b1" : "#333" }}>
                        {entityKeys.length === 0 ? `Select ${entityTypeFilter === "CONSUMER" ? "consumer" : "generator"}…` : `${entityKeys.length} selected`}
                      </span>
                      <span style={{ fontSize: 9 }}>▼</span>
                    </div>
                    {/* Selected chips (≤ 5) */}
                    {entityKeys.length > 0 && entityKeys.length <= 5 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                        {selectedEntities.map((entity) => (
                          <span key={entity.key} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: bgSel, color: accent, border: `1px solid ${entityTypeFilter === "CONSUMER" ? "#91a8ac" : "#ffe4d4"}`, borderRadius: 100, fontSize: 11, padding: "2px 8px" }}>
                            {entity.name}
                            <Close style={{ fontSize: 12, cursor: "pointer" }} onClick={() => setEntityKeys((prev) => prev.filter((k) => k !== entity.key))} />
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Dropdown panel */}
                    {entityDropdownOpen && (
                      <div style={{ position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", zIndex: 200 }}>
                        <div style={{ padding: "6px 8px", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 8, position: "sticky", top: 0, background: "#fff" }}>
                          <input type="text" placeholder={`Search ${entityTypeFilter === "CONSUMER" ? "consumer" : "generator"}…`} value={entitySearch} onChange={(e) => setEntitySearch(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ flex: 1, boxSizing: "border-box", border: "1px solid #e2e2e5", borderRadius: 4, padding: "6px 10px", fontSize: 12, outline: "none", fontFamily: "inherit", color: "#333" }} />
                          <span
                            style={{ fontSize: 11, color: accent, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", textDecoration: "underline" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (allSelected) setEntityKeys((prev) => prev.filter((k) => !allKeys.includes(k)));
                              else setEntityKeys((prev) => { const s = new Set(prev); return [...prev, ...allKeys.filter((k) => !s.has(k))]; });
                            }}
                          >
                            {allSelected ? "Deselect All" : "Select All"}
                          </span>
                        </div>
                        <div style={{ maxHeight: 200, overflowY: "auto" }}>
                          {list.length === 0 && <div style={{ padding: 12, fontSize: 12, color: "#8c8a96" }}>No {entityTypeFilter === "CONSUMER" ? "consumers" : "generators"} found</div>}
                          {entityTypeFilter === "CONSUMER"
                            ? list.map((c) => { const key = `C:${c.consumerId}`; const isSel = entityKeys.includes(key); return (
                              <div key={key} style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: isSel ? bgSel : "#fff", fontSize: 12 }} onClick={() => setEntityKeys((prev) => isSel ? prev.filter((k) => k !== key) : [...prev, key])}>
                                <input type="checkbox" checked={isSel} readOnly style={{ accentColor: accent, flexShrink: 0 }} />
                                <span style={{ width: 16, height: 16, borderRadius: "50%", background: accent, color: "#fff", display: "inline-grid", placeItems: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{badge}</span>
                                <span style={{ flex: 1 }}>{c.consumerName || c.consumerId}</span>
                              </div>
                            ); })
                            : list.map((g) => { const key = `G:${g.generatorId}`; const isSel = entityKeys.includes(key); return (
                              <div key={key} style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", background: isSel ? bgSel : "#fff", fontSize: 12 }} onClick={() => setEntityKeys((prev) => isSel ? prev.filter((k) => k !== key) : [...prev, key])}>
                                <input type="checkbox" checked={isSel} readOnly style={{ accentColor: accent, flexShrink: 0 }} />
                                <span style={{ width: 16, height: 16, borderRadius: "50%", background: accent, color: "#fff", display: "inline-grid", placeItems: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>{badge}</span>
                                <span style={{ flex: 1 }}>{g.generatorName || g.generatorId}</span>
                              </div>
                            ); })
                          }
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div style={{ borderTop: "1px dashed #e8e8e8", margin: "0 0 16px" }} />

        {/* Step 2: Product & Config */}
        <div className={Styles.fgroup}>
          <div className={Styles.fgroupTitle}>
            <span style={{ background: "#f1935c", color: "#fff", width: 20, height: 20, borderRadius: "50%", display: "inline-grid", placeItems: "center", fontSize: 10, fontWeight: 700 }}>2</span>
            &nbsp;Select Product &amp; Configuration
          </div>
          <div className={Styles.grid2}>
            <DataSelect label="Product" name="selProductId" value={selProductId} onChange={(e) => setSelProductId(e.target.value)} items={products} getValue={(p) => p.id} getLabel={(p) => `${p.productType} (#${p.id})`} />
            <DataSelect label="Payment Configuration" name="selConfigId" value={selConfigId} onChange={(e) => setSelConfigId(e.target.value)} items={configurations} getValue={(c) => c.id} getLabel={(c) => `${c.configurationName} (#${c.id})`} />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#6f6d7c" }}>
              <input type="checkbox" id="replaceExisting" checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} style={{ accentColor: "#f1935c", width: 15, height: 15 }} />
              Replace existing mappings
            </label>
            <button className={`${Styles.btnGhost} ${Styles.btnSm}`} onClick={handleAddEntry} disabled={!canAdd}>
              <Add style={{ fontSize: 14 }} /> Add to list
            </button>
          </div>
        </div>

        {entries.length > 0 && (
          <div style={{ border: "1px solid #e0e0e0", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "8px 14px", background: "#34656d", color: "#fff", fontSize: 12, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Mappings to submit</span>
              <span style={{ background: "#fff", color: "#34656d", borderRadius: 100, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>{entries.length}</span>
            </div>
            {entries.map((entry, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", borderBottom: i < entries.length - 1 ? "1px solid #f5f5f5" : "none", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                  <Chip label={entry.entityType} variant={entry.entityType === "CONSUMER" ? "teal" : "orange"} />
                  <span style={{ color: "#444" }}>Entity <strong>#{entry.entityId}</strong></span>
                  <span style={{ color: "#ccc" }}>›</span>
                  <span style={{ color: "#444" }}>Config <strong>#{entry.configurationId}</strong></span>
                </div>
                <button className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`} onClick={() => removeEntry(i)} title="Remove"><Delete style={{ fontSize: 14 }} /></button>
              </div>
            ))}
          </div>
        )}
      </FormDialog>

      {/* Edit mapping dialog */}
      <FormDialog
        open={!!editRow}
        onClose={() => { setEditRow(null); setEditConfigId(""); }}
        title="Edit Mapping"
        onSubmit={() => {
          if (!editRow || !editConfigId) return;
          dispatch(removeSingleMapping({ productId: viewProductId, payload: { configurationId: editRow.configurationId, entityId: editRow.entityId, entityType: editRow.entityType } }))
            .unwrap().then(() => dispatch(saveMappings({ productId: viewProductId, payload: { productId: Number(viewProductId), replaceExisting: false, mappings: [{ configurationId: Number(editConfigId), entityId: editRow.entityId, entityType: editRow.entityType }] } })).unwrap())
            .then(() => SuccessMessage("Mapping updated")).catch((err) => ErrorMessage(err || "Failed"));
          setEditRow(null); setEditConfigId("");
        }}
        submitLabel="Update"
      >
        {editRow && (
          <div className={Styles.formGrid}>
            <div className={Styles.field}>
              <label className={Styles.fieldLabel}>Entity</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 6 }}>
                <Chip label={editRow.entityType} variant={editRow.entityType === "CONSUMER" ? "teal" : "orange"} />
                <span style={{ fontSize: 13, color: "#444" }}>#{editRow.entityId}</span>
              </div>
            </div>
            <DataSelect label="Payment Configuration" name="editConfigId" value={editConfigId} onChange={(e) => setEditConfigId(e.target.value)} items={configurations} getValue={(c) => c.id} getLabel={(c) => `${c.configurationName} (#${c.id})`} />
          </div>
        )}
      </FormDialog>

      {/* Mappings table */}
      <div className={`${Styles.card} ${Styles.cardFill}`}>
        <div className={Styles.tableScroll}>
          {filteredMappings.length === 0 && currentMappings.length === 0 ? (
            <div className={Styles.emptyState}>
              <div className={Styles.emptyGlyph}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="2" y="4" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" /><rect x="13" y="12" width="7" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" /><path d="M9 7h3a2 2 0 012 2v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              </div>
              <div className={Styles.emptyTitle}>No mappings for this product yet</div>
              <div className={Styles.emptyDesc}>Connect a company entity to this product and a payment configuration to start settling trades against it.</div>
              <button className={Styles.btnPrimary} onClick={openDialog}><Add style={{ fontSize: 15 }} /> Add the first mapping</button>
            </div>
          ) : (
            <DataTable
              columns={["Configuration", "Company", "Entity", "Role", "Actions"]}
              rows={filteredMappings}
              renderRow={(row) => (
                <>
                  <td className={Styles.td}>
                    <span className={Styles.codeChip}>{row.configurationName || `Config #${row.configurationId}`}</span>
                    <div style={{ fontSize: 10, color: "#8c8a96", marginTop: 2, fontFamily: "monospace" }}>#{row.configurationId}</div>
                  </td>
                  <td className={Styles.td} style={{ fontWeight: 500 }}>{row.companyName}</td>
                  <td className={Styles.td} style={{ fontWeight: 500 }}>{row.entityName || entityNameMap[row.entityId] || `#${row.entityId}`}</td>
                  <td className={Styles.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Chip label={row.entityType} variant={row.entityType === "CONSUMER" ? "teal" : "orange"} />
                      <span style={{ fontSize: 11, color: "#8c8a96", fontFamily: "monospace" }}>#{row.entityId}</span>
                    </div>
                  </td>
                  <td className={Styles.td}>
                    <div className={Styles.rowActs}>
                      <button className={`${Styles.iconBtn} ${Styles.iconBtnDanger}`} onClick={() => handleDeleteSingle(row)} title="Remove">
                        <Delete style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  </td>
                </>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ConfigurePlatform = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  const companies = useSelector((state) => state?.gnaAdminSlice?.fetchCompany?.data);
  const products = useSelector(selectProducts);
  const paymentConfigurations = useSelector(selectPaymentConfigurations);
  const paymentConfigRules = useSelector(selectPaymentConfigRules);
  const mappings = useSelector(selectMappings);
  const loading = useSelector(selectPlatformLoading);
  const wizard = useSelector(selectWizard);

  const fetchForTab = (tab) => {
    if (tab === 0) dispatch(fetchCompany());
    else if (tab === 1) dispatch(fetchAllProducts());
    else if (tab === 2) { dispatch(fetchAllPaymentConfigurations()); dispatch(fetchAllPaymentConfigRules()); }
  };

  useEffect(() => {
    if (open) fetchForTab(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const TABS = ["Mappings", "Products", "Payment Configurations & Rules"];
  const tabCounts = [
    (mappings[wizard.productId] || []).length,
    (products || []).length,
    (paymentConfigurations || []).length,
  ];

  return (
    <div className={Styles.modalBackdrop} onClick={handleClose}>
      <div className={Styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* White title bar */}
        <div className={Styles.modalBar}>
          <div>
            <span className={Styles.kicker}>Platform administration</span>
            <span className={Styles.modalTitle}>Configure Platform</span>
          </div>
          <button className={Styles.closeBtn} onClick={handleClose}>
            <Close fontSize="small" />
          </button>
        </div>

        {/* Teal context band: tabs */}
        <div className={Styles.ctxBand}>
          <div className={Styles.tabsRow}>
            {TABS.map((label, i) => (
              <button
                key={label}
                className={`${Styles.tabBtn} ${activeTab === i ? Styles.tabBtnActive : ""}`}
                onClick={() => { setActiveTab(i); fetchForTab(i); }}
                aria-current={activeTab === i ? "true" : undefined}
              >
                {label}
                <span className={Styles.tabBadge}>{tabCounts[i] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className={Styles.loaderBox}>
            <div className={Styles.spinner} />
          </div>
        )}

        {/* Tab content */}
        <div className={Styles.body}>
          {activeTab === 0 && (
            <MappingsTab
              allMappings={mappings}
              products={products}
              configurations={paymentConfigurations}
              companies={companies}
              rules={paymentConfigRules}
              dispatch={dispatch}
            />
          )}
          {activeTab === 1 && (
            <ProductsTab products={products} dispatch={dispatch} wizard={wizard} />
          )}
          {activeTab === 2 && (
            <PaymentSplitTab
              configurations={paymentConfigurations}
              rules={paymentConfigRules}
              dispatch={dispatch}
              wizard={wizard}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurePlatform;
